import { Editor, Transforms, Node } from 'slate';
import { nanoid } from 'nanoid';

// Create quiz's option
const createOption = () => ({
	type: 'quiz-option',
	key: nanoid(),
	correct: false,
	children: [{ text: '' }]
});

const createContent = () => ({
	type: 'quiz-content',
	children: [{ text: '' }]
});

// Create quiz's question with options
const createQuestion = () => ({
	type: 'quiz-question',
	key: nanoid(),
	children: [createContent(), createOption()]
});

// Create quiz with rows
const createQuiz = () => ({
	type: 'quiz',
	key: nanoid(),
	children: [createQuestion()]
});

const withQuiz = editor => {
	const {
		insertBreak
	} = editor;

	// Check if selection is in quiz
	editor.isInQuiz = () => {
		const [quiz] = Editor.nodes(editor, {
			match: node => node.type === 'quiz'
		});
	
		return !!quiz;
	};

	// Handle's break event inside quiz
	// @args: default arguments
	editor.insertBreak = (...args) => {
		// Run default delete fuction if not in quiz
		if (!editor.isInQuiz()) return insertBreak(...args);

		// In quiz on enter insert only a line break
		Transforms.insertText(editor, '\n');
	};

	// Insert quiz in current selection
	editor.insertQuizVoid = () => {
		// Do not add quiz field if selection is undefined
		if (!editor.selection || editor.isInQuiz()) return;

		// Find current element
		const [current] = Editor.above(editor);

		// Find next element
		const next = Editor.next(editor);

		// Remove empty node
		if (Node.string(current) === '') {
			Transforms.removeNodes(editor);
		}

		// Insert quiz in current selection
		Transforms.insertNodes(editor, {
			type: 'quiz-void',
			children: [{ text: '' }]
		});

		// Insert empty paragraph node if there is no next node
		if (!next) {
			Transforms.insertNodes(editor, {
				type: 'paragraph',
				children: [{ text: '' }]
			}, {
				at: [editor.children.length]
			});
		}
	};

	// Insert quiz in current selection
	editor.insertQuiz = () => {
		// Do not add quiz field if selection is undefined
		if (!editor.selection || editor.isInQuiz()) return;

		// Create quiz template
		const quiz = createQuiz();

		// Find current element
		const [current] = Editor.above(editor);

		// Find next element
		const next = Editor.next(editor);

		// Remove empty node
		if (Node.string(current) === '') {
			Transforms.removeNodes(editor);
		}

		// Insert quiz in current selection
		Transforms.insertNodes(editor, quiz);

		// Insert empty paragraph node if there is no next node
		if (!next) {
			Transforms.insertNodes(editor, {
				type: 'paragraph',
				children: [{ text: '' }]
			}, {
				at: [editor.children.length]
			});
		}
	};

	// Removes quiz with rows and columns
	editor.deleteQuiz = () => {
		// Only run if selection is in quiz
		if (!editor.isInQuiz()) return;

		// Remove quiz with all questions and options
		Transforms.removeNodes(editor, {
			match: node => node.type === 'quiz'
		});
	};

	editor.insertQuizQuestion = path => {
		// Find current element
		const [quiz] = Editor.node(editor, path, {
			match: node => node.type === 'quiz'
		});

		// Create question template
		const question = createQuestion();

		// Insert question after all questions
		Transforms.insertNodes(editor, question, {
			at: [path[0], quiz.children?.length]
		});
	};

	editor.insertQuizOption = path => {
		// Find current element
		const [question] = Editor.node(editor, path, {
			match: node => node.type === 'quiz-question'
		});

		// Create option template
		const option = createOption();

		// Insert option in current question
		Transforms.insertNodes(editor, option, {
			at: [...path, question.children.length],
		});
	};

	editor.deleteQuizQuestion = path => {
		// Find current element
		const [quiz] = Editor.above(editor, {
			at: path,
			match: node => node.type === 'quiz'
		});

		// Removal type
		const type = quiz.children.length === 1 ? 'quiz' : 'quiz-question';

		// Remove question
		Transforms.removeNodes(editor, {
			at: path,
			voids: true,
			match: node => node.type === type
		});
	};

	editor.deleteQuizOption = path => {
		Transforms.removeNodes(editor, {
			at: path,
			voids: true,
			match: node => node.type === 'quiz-option'
		});
	};

	editor.changeQuizMode = mode => {
		if (editor.quizMode) editor.quizMode = mode;
	};

	return editor;
};

export default withQuiz;